import React, { useEffect, useState } from "react";
import { Button, Dropdown, Form, Modal, Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { axiosInstance } from "../../helpers/axios/axios";
import {
  LogisticsClientsURL,
  LogisticsCompaniesURL,
} from "../../helpers/endpoints/api_endpoints";

const POExportModal = ({ show, handleClose, handleExport, currentFilters }) => {
  // const [clients, setClients] = useState([]); // Removed: Replaced with search logic
  const [companies, setCompanies] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showInOutData, setShowInOutData] = useState(false);

  // --- New State for Client Search ---
  const [clientQuery, setClientQuery] = useState("");
  const [clientSuggestions, setClientSuggestions] = useState([]);
  // ------------------------------------

  const { designation } = useSelector((state) => state.account);

  useEffect(() => {
    // fetchClients(); // Removed: No longer needed on initial load
    fetchCompanies();
  }, []);

  // Initialize modal only when it first opens
  useEffect(() => {
    if (show) {
      console.log("Modal opening with currentFilters:", currentFilters);

      const clientId = currentFilters?.client || "";
      setSelectedClient(clientId);

      // If a client ID is passed from filters, fetch its details to show the name
      if (clientId) {
        axiosInstance
          .get(`${LogisticsClientsURL}${clientId}/`)
          .then((response) => {
            setClientQuery(response.data.name);
          })
          .catch((err) => {
            console.error("Error fetching client details:", err);
            setClientQuery(""); // Clear on error
          });
      } else {
        setClientQuery(""); // No ID, so clear input
      }

      setSelectedCompany(currentFilters?.company || "");
      setStartDate(currentFilters?.startDate || "");
      setEndDate(currentFilters?.endDate || "");
      setShowInOutData(false);
      setClientSuggestions([]); // Always clear suggestions on modal open

      console.log("Modal initialized with:");
      console.log("- selectedClient:", clientId);
      console.log("- selectedCompany:", currentFilters?.company || "");
      console.log("- startDate:", currentFilters?.startDate || "");
      console.log("- endDate:", currentFilters?.endDate || "");
    }
  }, [show]); // Only depend on show, not currentFilters

  // Reset modal state when it closes
  useEffect(() => {
    if (!show) {
      setSelectedClient("");
      setSelectedCompany("");
      setStartDate("");
      setEndDate("");
      setShowInOutData(false);
      // Reset new search states
      setClientQuery("");
      setClientSuggestions([]);
    }
  }, [show]);

  /* Removed: Replaced with fetchClientSuggestions
  const fetchClients = async () => {
    try {
      const response = await axiosInstance.get(LogisticsClientsURL);
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };
  */

  const fetchCompanies = async () => {
    try {
      const response = await axiosInstance.get(LogisticsCompaniesURL);
      setCompanies(response.data);
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  // --- New Client Search Functions ---

  // Fetches client suggestions from the API based on query
  const fetchClientSuggestions = async (query) => {
    try {
      const { data } = await axiosInstance.get(LogisticsClientsURL, {
        params: { name: query },
      });
      setClientSuggestions(data.results || []);
    } catch (err) {
      console.error("Client search error:", err);
      setClientSuggestions([]);
    }
  };

  // Debounce useEffect hook for client search
  useEffect(() => {
    const handler = setTimeout(() => {
      // Only fetch if the query text doesn't match the name of the already selected client
      // This prevents re-fetching when a user selects an item
      if (
        clientQuery &&
        clientSuggestions.find((s) => s.name === clientQuery) === undefined
      ) {
        fetchClientSuggestions(clientQuery);
      } else if (!clientQuery) {
        setClientSuggestions([]); // Clear suggestions if input is empty
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [clientQuery]);

  // Handler to fetch initial list on focus
  const handleClientFocus = () => {
    if (!clientQuery) {
      fetchClientSuggestions(""); // Fetch initial list on focus
    }
  };
  // ------------------------------------

  const handleExportClick = async () => {
    // Validate date fields when Show In/Out Data is checked
    if (showInOutData && (!startDate || !endDate)) {
      alert(
        "Start Date and End Date are mandatory when 'Show In/Out Data' is selected."
      );
      return;
    }

    // Debug: Log what we're sending
    const exportData = {
      selectedClient,
      selectedCompany,
      startDate,
      endDate,
      showInOutData,
    };

    console.log("Modal sending export data:", exportData);
    console.log("Modal startDate value:", startDate);
    console.log("Modal endDate value:", endDate);
    console.log("Modal selectedClient value:", selectedClient);
    console.log("Modal selectedCompany value:", selectedCompany);
    console.log("Modal showInOutData value:", showInOutData);

    setIsLoading(true);
    try {
      await handleExport(exportData);
      handleClose();
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if export button should be disabled
  const isExportDisabled =
    isLoading || (showInOutData && (!startDate || !endDate));

  const handleResetFilters = () => {
    setSelectedClient("");
    setSelectedCompany("");
    setStartDate("");
    setEndDate("");
    setShowInOutData(false);
    setClientQuery(""); // Reset new search state
    setClientSuggestions([]);
  };

  // Check if user is director
  const isDirector = designation === "Director";

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Select Filters for Export</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Modal.Title className="text-muted small mb-3">
          Filters (optional)
        </Modal.Title>

        {/* --- Replaced Client Dropdown with Search Input --- */}
        <Form.Group className="mb-3">
          <Form.Label className="text-muted small">Client</Form.Label>
          <div className="position-relative">
            <Form.Control
              type="text"
              placeholder="Search client..."
              value={clientQuery} // The text input shows the query state
              onChange={(e) => {
                setClientQuery(e.target.value);
                // If user types, we de-select the client ID until they pick a new one
                if (selectedClient) {
                  setSelectedClient("");
                }
              }}
              onFocus={handleClientFocus}
            />
            {clientSuggestions.length > 0 && (
              <ul
                className="list-group position-absolute w-100"
                style={{
                  top: "100%",
                  maxHeight: 200,
                  overflowY: "auto",
                  zIndex: 1055, // Ensure this z-index is higher than the modal body
                }}
              >
                {clientSuggestions.map((client) => (
                  <li
                    key={client.id}
                    className="list-group-item list-group-item-action p-2"
                    onClick={() => {
                      setSelectedClient(client.id); // Set the selected ID
                      setClientQuery(client.name); // Update input field to show the selected name
                      setClientSuggestions([]); // Close the suggestion list
                    }}
                  >
                    {client.name}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Form.Group>
        {/* --------------------------------------------------- */}

        <Form.Label className="text-muted small">Company</Form.Label>
        <Dropdown onSelect={(e) => setSelectedCompany(e)} className="mb-2">
          <Dropdown.Toggle
            variant="outline-secondary"
            id="dropdown-basic"
            className="w-100 d-flex justify-content-between align-items-center"
          >
            {companies?.results?.find(
              (company) => company.id === parseInt(selectedCompany)
            )?.name || "Select Company"}
          </Dropdown.Toggle>
          <Dropdown.Menu className="w-100">
            {companies?.results?.map((company) => (
              <Dropdown.Item key={company.id} eventKey={company.id}>
                {company.name}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>

        <Form.Label className="text-muted small">Start Date</Form.Label>
        <Form.Control
          className="mb-2"
          type="date"
          value={startDate}
          onChange={(e) => {
            console.log("Start date changed to:", e.target.value);
            setStartDate(e.target.value);
          }}
          placeholder="Start Date"
          required={showInOutData}
        />
        {showInOutData && !startDate && (
          <div className="text-danger small mb-2">
            Start Date is required when "Show In/Out Data" is selected
          </div>
        )}

        <Form.Label className="text-muted small">End Date</Form.Label>
        <Form.Control
          className="mb-2"
          type="date"
          value={endDate}
          onChange={(e) => {
            console.log("End date changed to:", e.target.value);
            setEndDate(e.target.value);
          }}
          placeholder="End Date"
          required={showInOutData}
        />
        {showInOutData && !endDate && (
          <div className="text-danger small mb-2">
            End Date is required when "Show In/Out Data" is selected
          </div>
        )}
        {/* Show In/Out Data Checkbox - Only for Directors */}
        {isDirector && (
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="showInOutData"
              label="Show In/Out Data"
              checked={showInOutData}
              onChange={(e) => setShowInOutData(e.target.checked)}
            />
            <Form.Text className="text-muted">
              When checked, exports detailed inward/outward data using all
              current filters from the main page. This includes client, company,
              warehouse, status, ATM ID, type, docket numbers, state, and date
              range. Date range is mandatory for this export type.
            </Form.Text>
          </Form.Group>
        )}

        <div className="d-flex justify-content-start mb-2">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
            className="me-2"
          >
            Close
          </Button>
          <Button
            variant="secondary"
            onClick={handleResetFilters}
            disabled={isLoading}
          >
            Reset Filters
          </Button>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="primary"
          onClick={handleExportClick}
          disabled={isExportDisabled}
        >
          {isLoading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Exporting…
            </>
          ) : (
            "Export"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default POExportModal;